import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { ChartPoint } from 'src/app/services/rest-api/rest-api';
import { transformPriceForChartAxis } from '../functions';
import { externalTooltipHandler } from './custom-hover-tooltip';
import 'dayjs/plugin/localeData';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';
import { BaseChartDirective } from 'ng2-charts';
import dayjs from 'dayjs';
import { desktopWidthBreakpoint } from '../constants';

@Component({
  selector: 'libero-charts',
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.scss'],
  standalone: false
})
export class ChartsComponent implements OnChanges, AfterViewInit {
  @Input() public chartData!: ChartPoint[];
  @Input() public xLabels!: Date[];
  @Input() public deposits?: ChartPoint[];
  @Input() public userLocalization: 'CZK' | 'EUR' | string = 'CZK';
  @Input() public showDeposits = true;
  @Input() public isPercent = false;
  @Input() public depositsLinePoints?: ChartPoint[];

  private xLabelsVisibility: boolean[] = [];
  private displayFullPercent = false;

  @ViewChild(BaseChartDirective) private chart!: BaseChartDirective;

  public lineChartData?: ChartConfiguration['data'];

  public lineChartOptions: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    hover: {
      mode: 'nearest',
      intersect: false,
    },
    scales: {
      xAxis: {
        type: 'time',
        ticks: {
          source: 'labels',
          autoSkip: false,
          maxRotation: 0,
        },
        grid: {
          color: 'transparent'
        }
      },
      yAxis: {
        beginAtZero: true,
        ticks: {
          callback: (tickValue: string | number) => {
            return transformPriceForChartAxis(tickValue, 'CZK', 'cs-CZ', this.isPercent, 'standard', this.displayFullPercent)
          },
        },
      },
    },
    plugins: {
      tooltip: {
        enabled: false,
        position: 'nearest',
        external: (context) => externalTooltipHandler(context, 'basicChart', this.isPercent, this.displayFullPercent),
      },
    },
  };

  public ngAfterViewInit(): void {
    this.captureDefaultTicks();
  }

  private captureDefaultTicks(): void {
    if(!this.chart || !this.chart.chart || !this.lineChartOptions || !this.lineChartOptions.scales || !this.lineChartOptions.scales['xAxis'] || !this.lineChartOptions.scales['xAxis'].ticks) {
      console.error('Error while generating chart. Chart ref. or scales data are missing.')
      return;
    }

      this.markDatesToView();
      this.markPercentValuesToView();

      this.lineChartOptions = {
        ...this.lineChartOptions,
        scales: {
          ...this.lineChartOptions.scales,
          xAxis: {
            ...this.lineChartOptions.scales['xAxis'],
            ticks: {
              ...this.lineChartOptions.scales['xAxis'].ticks,
              callback: (value: string | number, index: number) => {
                dayjs.locale(this.userLocalization === 'EUR' ? 'en' : 'cs');

                if(!this.xLabelsVisibility[index]) {
                  return '';
                }

                try {
                  const isoLabels = this.xLabels.map(label => label.toISOString());
                  const uniqueYears = new Set(isoLabels.map(xLabel => dayjs(xLabel).year()));
                  const yearDifference = Math.max(...uniqueYears) - Math.min(...uniqueYears);
                  const dateValue = dayjs(isoLabels[index]);

                  // more years than #value should not display duplicit years
                  const hideMonthAfterYear = 5;

                  if(yearDifference > 1) {
                    if (window.innerWidth > desktopWidthBreakpoint) {
                      return dateValue.format('MMMM YYYY');
                    } else if (yearDifference < hideMonthAfterYear) {
                      return dateValue.format('M/YYYY');
                    }
                    return dateValue.format('YYYY');
                  } else {
                    if (window.innerWidth > desktopWidthBreakpoint) {
                      return dateValue.format('DD. MMMM YYYY');
                    }
                    return dateValue.format('DD. MM. YY');
                  }
                } catch (error) {
                  console.error('Wrong chart label!', error);
                  return value;
                }
              }
            }
          }
        }
      };

      this.chart.chart.draw();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // todo - gradient chart shadow, not single color
    const bgCol = 'rgba(148,159,177,0.2)';

    // if (this.chartRef) {
    //   // console.log("reff");
    //   const canvasElement = (<any>this.chartRef).nativeElement;

    //   const ctx = canvasElement.getContext('2d');
    //   const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    //   gradient.addColorStop(0, 'rgba(255, 99, 132, 0.2)');
    //   gradient.addColorStop(1, 'rgba(255, 99, 132, 0.6)');

    //   bgCol = gradient;
    // }

    const datasets: ChartDataset[] = [
      {
        data: this.chartData,
        borderColor: '#262626',
        pointRadius: 0,
        pointHoverRadius: 11,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#E52713',
        pointHoverBorderColor: '#E52713',
        pointHoverBorderWidth: 5,
        fill: 'origin',
        // shadowColor: 'rgba(0, 0, 0, 0.5)',
        // shadowBlur: 5
        // shadowColor: 'rgba(0, 0, 0,1)',
        // shadowBlur: 10

        // backgroundColor: "linear-gradient(180deg, black 0%, blue 100%)",
        backgroundColor: bgCol,

      },
    ]

    if (changes['showDeposits']?.currentValue && this.deposits && this.depositsLinePoints) {
      // const deposits = this.deposits;
      // const pointsRadius = Array(deposits.length).fill(6);

      // // add start point (with hidden point)
      // deposits.unshift({
      //   x: this.chartData[0].x,
      //   y: this.chartData[0].y,
      // })
      // pointsRadius.unshift(0);

      datasets.push(
        {
          data: this.deposits,
          borderColor: '#b5b5b5',
          // showLine: true,
          showLine: false,
          pointBackgroundColor: '#262626',
          // pointHoverBackgroundColor: '#262626',
          pointRadius: 6,
          // pointRadius: pointsRadius,
          // pointHoverRadius: 6,
          // pointHoverBorderWidth: 0,
          // pointBorderColor: 'transparent',
          // pointBorderWidth: 7,
          pointHoverRadius: 11,
        },
      );

      datasets.push(
        {
          data: this.depositsLinePoints,
          borderColor: '#b5b5b5',
          showLine: true,
          pointBackgroundColor: '#262626',
          pointRadius: 0,
          pointHoverRadius: 11,
        },
      );
    }

    if (this.chartData && this.xLabels) {
      this.lineChartData = {
        datasets,
        labels: this.xLabels,
      };
    };
  }

  /**
   * Force display decimal for small percents ranges that would display duplicit labels
   */
  private markPercentValuesToView(): void {
    const yValues = this.chartData.map(d => d.y);
    const min = Math.min(...yValues);
    const max = Math.max(...yValues);
    this.displayFullPercent = max - min < 6;
  }

  private markDatesToView() {
    // fixme - prevent duplicit years
    let numSelections = 1;

    if (window.innerWidth >= 1200) {
      numSelections = 4;
    } else if (window.innerWidth > 580) {
      numSelections = 3;
    } else if (window.innerWidth > 380) {
      numSelections = 2;
    }

    // check if there is big gap between any values, then its not possible to guess what is next label so hide all but first and last to prevent overflowing
    let maxGap = 0;
    let totalGap = 0;

    for (let i = 1; i < this.xLabels.length; i++) {
      const gapInDays = (this.xLabels[i].getTime() - this.xLabels[i - 1].getTime()) / (1000 * 3600 * 24);
      totalGap += gapInDays;

      if (gapInDays > maxGap) {
          maxGap = gapInDays;
      }
    }
    const averageGapInDays = totalGap / (this.xLabels.length - 1);

    if(this.xLabels.length < 10 || maxGap > averageGapInDays * 5) {
      numSelections = 0;
    }

    // mark first and last labels as always displayed
    this.xLabelsVisibility = Array(this.xLabels.length).fill(false);
    this.xLabelsVisibility[0] = true;
    this.xLabelsVisibility[this.xLabelsVisibility.length - 1] = true;

    const numToSelect = Math.min(numSelections, this.xLabels.length - 2);
    const startDate = new Date(this.xLabels[0]).getTime();
    const endDate = new Date(this.xLabels[this.xLabels.length - 1]).getTime();
    const interval = (endDate - startDate) / (numToSelect + 1);

    for (let i = 1; i <= numToSelect; i++) {
        const targetTime = startDate + i * interval;
        let closestIndex = 1;
        let smallestDifference = Math.abs(new Date(this.xLabels[1]).getTime() - targetTime);

        for (let j = 2; j < this.xLabels.length - 1; j++) {
            const currentDifference = Math.abs(new Date(this.xLabels[j]).getTime() - targetTime);
            if (currentDifference < smallestDifference) {
                closestIndex = j;
                smallestDifference = currentDifference;
            }
        }

        this.xLabelsVisibility[closestIndex] = true;
    }
  }
}
